import { FloatingFormItem, Group, InputDecimal } from '@/components/_common';
import ManufacturerInfiniteSelectField from '@/components/_common/ManufacturerInfiniteSelectField';
import { DirectSaleStockItem } from '@/core/domain/sales/DirectSale/DirectSale.types';
import formatterPrice from '@/utils/number/formatterPrice';
import { Alert, Button, Col, Form, FormInstance, Row, Typography } from 'antd';
import { useIntl } from 'react-intl';
import { useDirectSaleItemPermissions } from './directSaleItemPermissions';
import { useMemo, useEffect } from 'react';
import { PartStockPrices } from '@/core/domain/stock/StockItems/StockItems.types';
import { UseMutationResult } from 'react-query';
import SpaceForButton from '@/components/_common/SpaceForButtons';
import DirectSalePartsHistoryModal from '../DirectSalePartsHistoryModal';
import { useDefaultEmptyFieldError, useDisclosure } from '@/hooks';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import Authorities from '@/types/Authority';

interface DirectSaleAddPartPriceFormProps {
  form?: FormInstance<DirectSaleStockItem>;
  avaliableQuantityItem?: number;
  isPartSelected: boolean;
  companyId?: number;
  partStockPrices?: UseMutationResult<
    PartStockPrices,
    unknown,
    number | undefined
  >;
  sellingPriceItemSelectedEdition?: number;
}

export default function DirectSaleAddPartPriceForm({
  form,
  avaliableQuantityItem,
  isPartSelected,
  companyId,
  partStockPrices,
  sellingPriceItemSelectedEdition,
}: DirectSaleAddPartPriceFormProps) {
  const intl = useIntl();
  const itemPermissions = useDirectSaleItemPermissions();

  const [modalOpened, modalAction] = useDisclosure();

  const partId = Form.useWatch(['partId'], form);
  const partDescription = Form.useWatch(['description'], form);
  const totalStock = Form.useWatch(['totalStock'], form) || 0;
  const unitaryValue = Form.useWatch(['unitaryValue'], form) || 0;
  const discount = Form.useWatch(['discount'], form) || 0;
  const discountCost = Form.useWatch(['discountCost'], form) || 0;

  const defaultEmptyFieldError = useDefaultEmptyFieldError();

  const subTotal = totalStock * unitaryValue;

  const totalValue = useMemo(() => {
    const appliedDiscount = discountCost || (subTotal * (discount || 0)) / 100;
    return subTotal - appliedDiscount;
  }, [subTotal, discount, discountCost]);

  const stockPrices = useMemo(
    () =>
      partStockPrices?.data?.result.find(
        (part) => part.company.id === companyId,
      ),
    [companyId, partStockPrices?.isLoading, partStockPrices?.data],
  );

  const disabledDiscount = totalStock === 0;

  const hasLowValuePermission = hasPermissions([
    Authorities.MOVIMENTATION_SALE_ALLOW_TO_ENTER_ITEM_SALES_VALUE_BELOW_PROFIT_MARGIN,
  ]);

  const showPermissionAlert = useMemo(() => {
    if (hasLowValuePermission) return false;

    const minSellPrice =
      sellingPriceItemSelectedEdition || stockPrices?.sellPrice || 0;

    return !!partId && (totalValue === 0 || totalValue < minSellPrice);
  }, [hasLowValuePermission, stockPrices?.sellPrice, totalValue, partId]);

  useEffect(() => {
    form?.validateFields(['unitaryValue', 'discount', 'discountCost']);
  }, [totalValue, form]);

  const validateProfitMargin = useMemo(() => {
    return () => {
      if (hasLowValuePermission) return Promise.resolve();

      const minSellPrice =
        sellingPriceItemSelectedEdition || stockPrices?.sellPrice || 0;

      if (
        !!partId &&
        (totalValue === 0 ||
          (totalValue > 0 && minSellPrice > 0 && totalValue < minSellPrice))
      ) {
        return Promise.reject(
          new Error('Valor total abaixo da margem de lucro'),
        );
      }

      return Promise.resolve();
    };
  }, [hasLowValuePermission, stockPrices?.sellPrice, totalValue, partId]);

  return (
    <>
      <Row gutter={[24, 0]}>
        <Form layout="inline" form={form}>
          <Col span={24} lg={8} md={8}>
            <FloatingFormItem label="Fabricante" name="manufacturerId">
              <ManufacturerInfiniteSelectField disabled={!isPartSelected} />
            </FloatingFormItem>
          </Col>

          <Col span={24} lg={4} md={4}>
            <FloatingFormItem
              label={intl.formatMessage({ id: 'directSale.modal.form.qty' })}
              name="totalStock"
              initialValue={0}
              rules={[
                {
                  required: true,
                  type: 'number',
                  message: `Quantidade mínima: 0,01`,
                  min: 0.01,
                },
                {
                  required: true,
                  type: 'number',
                  max: avaliableQuantityItem,
                  message: `Quantidade máxima: ${avaliableQuantityItem || 0}`,
                },
              ]}
            >
              <InputDecimal disabled={isPartSelected} min={0} />
            </FloatingFormItem>
          </Col>

          <Col span={24} lg={4} md={4}>
            <FloatingFormItem
              label={intl.formatMessage({
                id: 'directSale.modal.form.unitaryValue',
              })}
              name="unitaryValue"
              initialValue={0}
              rules={[{ validator: validateProfitMargin }]}
            >
              <InputDecimal
                disabled={
                  !itemPermissions.editValueDirectSale || isPartSelected
                }
                min={0}
              />
            </FloatingFormItem>
          </Col>

          <Col span={24} lg={4} md={4}>
            <FloatingFormItem
              label={intl.formatMessage({
                id: 'directSale.modal.form.discount',
              })}
              name="discount"
              initialValue={0}
              rules={[
                { validator: validateProfitMargin },
                defaultEmptyFieldError,
              ]}
              getValueFromEvent={(value) => {
                const calc = (subTotal * (value || 0)) / 100;
                form?.setFieldValue('discountCost', calc || 0);
                return value;
              }}
            >
              <InputDecimal
                disabled={
                  !itemPermissions.editDiscountValue ||
                  isPartSelected ||
                  disabledDiscount
                }
                min={0}
                max={100}
              />
            </FloatingFormItem>
          </Col>

          <Col span={24} lg={4} md={4}>
            <FloatingFormItem
              label={intl.formatMessage({
                id: 'directSale.modal.form.discountCost',
              })}
              name="discountCost"
              initialValue={0}
              rules={[
                { validator: validateProfitMargin },
                defaultEmptyFieldError,
              ]}
              getValueFromEvent={(value) => {
                const discountPercent = subTotal
                  ? ((value || 0) / subTotal) * 100
                  : 0;

                form?.setFieldValue('discount', discountPercent || 0);

                return value;
              }}
            >
              <InputDecimal
                disabled={
                  !itemPermissions.editDiscountValue ||
                  isPartSelected ||
                  disabledDiscount
                }
                max={subTotal}
                min={0}
              />
            </FloatingFormItem>
          </Col>

          {showPermissionAlert && (
            <Col span={24}>
              <Alert
                type="warning"
                description={`Você não tem permissão para lançar valor total de venda do item abaixo da margem de lucro ${formatterPrice(
                  sellingPriceItemSelectedEdition ||
                    stockPrices?.sellPrice ||
                    0,
                )}`}
              />
            </Col>
          )}

          <Col span={24} style={{ display: 'flex', justifyContent: 'end' }}>
            <Form.Item noStyle shouldUpdate>
              <SpaceForButton justifyContent="space-between">
                <Button
                  type="primary"
                  onClick={modalAction.open}
                  disabled={!partId}
                >
                  Histórico
                </Button>

                <Group>
                  <Typography.Title level={4} style={{ marginTop: '2px' }}>
                    {intl.formatMessage({
                      id: 'directSale.modal.form.amount',
                    })}
                  </Typography.Title>
                  <span
                    style={{
                      color: 'rgb(160, 206, 51)',
                      fontSize: 20,
                      marginLeft: 14,
                    }}
                  >
                    {formatterPrice(totalValue)}
                  </span>
                </Group>
              </SpaceForButton>
            </Form.Item>
          </Col>
        </Form>
      </Row>

      <DirectSalePartsHistoryModal
        partId={partId}
        partDescription={partDescription}
        open={modalOpened}
        onCancel={modalAction.close}
      />
    </>
  );
}
